import { sortBy } from '@/utility.js';
import timetable from '@/timetable.js';
import { graphClient } from './login';
import { minutesNow, currentCycleDay } from '@/timeUtil.js';

// A lesson (or duty) is "on now" if today's cycle day matches and the current
// minute falls inside its interval. This is the one place we look at a clock.
function isOnNow(slot, when) {
    when = when || new Date();
    const day = currentCycleDay(when);
    if (day == null) return false;
    if (Number(slot.cycle_day_index) !== day) return false;
    const now = minutesNow(when);
    return slot.startMin != null && now >= slot.startMin && now < slot.endMin;
}

// Fetch a person's photo from Microsoft Graph as an object URL. Shared by Student
// and Teacher, which differ only in which email field they carry.
async function fetchPhotoURL(email, label) {
    if (import.meta.env.DEV && import.meta.env.VITE_USE_FIXTURE === 'true') return ''; // no Graph token on the fixture (MSAL bypassed); with MSAL on, photos load normally
    if (!email) return '';
    try {
        var imageBlob = await graphClient.api('users/' + email + '/photo/$value').get();
    } catch (e) {
        console.warn('Error getting photo for ' + label, email, e);
        return '';
    }
    return (window.URL || window.webkitURL).createObjectURL(imageBlob);
}

// Base for anything with a weekly schedule (subjects, students, teachers, rooms).
// Subclasses supply getTimeslots(); Teacher also overrides getDuties() to fold in
// yard duties. The day/rowKey/"now" filtering that used to be copy-pasted onto all
// four classes lives here once.
class SchedulableEntity {
    getTimeslots() { return []; }
    getDuties() { return []; }

    // Lessons (plus duties, for teachers) matching a cycle day + rowKey — or, when
    // called with no arguments, whatever is on right now.
    getLocation(cycle_day_index, rowKey) {
        if (typeof cycle_day_index === 'undefined') return this.getLocationAtTime();
        var matches = slot => Number(slot.cycle_day_index) === Number(cycle_day_index) && slot.rowKey === rowKey;
        return this.getTimeslots().filter(matches).concat(this.getDuties().filter(matches));
    }

    getLocationAtTime(when) {
        return this.getTimeslots().filter(t => isOnNow(t, when))
            .concat(this.getDuties().filter(d => isOnNow(d, when)));
    }
}

// Canvas course-id rules. A class's Canvas SIS id is normally `${year}_${code}`,
// but ~6.5 years of institutional exceptions override that. Kept as data so the
// special cases are scannable and easy to amend (see Subject.getCanvasURL).
// Any code containing one of these fragments has no Canvas course:
const CANVAS_EXCLUDED_CODES = ['ACT', 'STA', 'STB', 'STC', 'STD', 'STE', 'STF', 'STG'];
// SIS-id rewrites, applied in order (later matches win, mirroring the old if-ladder).
// `when(code)` decides if the rule applies; `to(code, year, current)` returns the id.
const CANVAS_COURSE_MAP = [
    // [0-9][0-9]TUT_1..4 -> the four house Canvas courses
    { when: code => code.indexOf('TUT_') !== -1, to: (code, year) => `${year}_${[null, 'BH', 'CN', 'KM', 'MS'][code.split('_')[1]]}_CANVAS` },
    { when: code => code.includes('AMP'), to: (code, year, current) => current.replace(/AMP_[0-9]+/, 'AMP_CANVAS') },
    { when: code => code.includes('07PER'), to: (code, year) => `${year}_07PER_CANVAS` },
    { when: code => code.includes('10CRI'), to: (code, year) => `${year}_10CRI_CANVAS` },
    { when: code => code.includes('11MUS'), to: (code, year) => `${year}_12MPE_1` },
    { when: code => code.includes('12VAD'), to: (code, year) => `${year}_12VAA_1` },
    { when: code => code.includes('11MAM_4'), to: (code, year) => `${year}_11MAM_3` },
    { when: code => code.includes('10CHC_1'), to: (code, year) => `${year}_11CHC_1` }, // 10CHC_1 & 11CHC_1 -> 11CHC_1
];

export class Subject extends SchedulableEntity {
    constructor(data) {
        super();
        Object.assign(this, data);
        if (!this.timeslots) this.timeslots = [];
        if (!this.teachers) this.teachers = [];
        this.type = 'SUBJECT';
    }

    getTeacher() {
        return this.getAllTeachers()[0];
    }
    getAllTeachers() {
        var teacher_short_names = new Set();
        this.timeslots.forEach(t => teacher_short_names.add(t.teacher_short_name));
        this.teachers.forEach(teacher_short_name => teacher_short_names.add(teacher_short_name));
        var teachers = Array.from(teacher_short_names).map(code => timetable.teachers[code]);
        teachers = teachers.filter(_ => _); // a teacher with no subjects won't exist in timetable.teachers
        return teachers;
    }

    getStudents() {
        return Object.values(timetable.students)
            .filter(x => x.subjects.indexOf(this.code) !== -1)
            .sort(sortBy('StudentSurname'));
    }

    getName() {
        return this.name || this.code;
    }

    getTimeslots() {
        return this.timeslots;
    }

    getYearLevel() {
        return parseInt(this.code.substr(0, 2));
    }

    getCanvasURL() {
        // Use the timetable's academic year, not the wall clock — otherwise every
        // senior Canvas link points at `${thisCalendarYear}_${code}` and breaks the
        // moment you view an archived timetable or cross a 1-Jan boundary.
        var year = (timetable.coordinate && timetable.coordinate.fileYear) || (new Date()).getFullYear();

        // No Canvas course at all for: explicit exceptions, non 7-12 year levels,
        // and the excluded code fragments.
        if (timetable.canvas_exceptions.some(e => e.ClassCode === this.code)) return null;
        if (![7, 8, 9, 10, 11, 12].includes(this.getYearLevel())) return null;
        if (CANVAS_EXCLUDED_CODES.some(frag => this.code.indexOf(frag) !== -1)) return null;

        // Default SIS id, then apply each matching institutional rewrite in order.
        var sisCourseId = year + '_' + this.code;
        CANVAS_COURSE_MAP.forEach(rule => {
            if (rule.when(this.code)) sisCourseId = rule.to(this.code, year, sisCourseId);
        });
        return `https://pulteney.instructure.com/courses/sis_course_id:${sisCourseId}`;
    }
}

export class Student extends SchedulableEntity {
    constructor(data) {
        super();
        Object.assign(this, data);
        if (!this.subjects) this.subjects = [];
        this.type = 'STUDENT';
    }

    getName() {
        return this.getPreferredName() + ' ' + this.StudentSurname;
    }

    getPhotoURL() {
        return fetchPhotoURL(this.StudentOccupEmail, 'student');
    }

    getPreferredName() { return this.StudentPreferred; }
    getFirstName() { return this.StudentPreferredFormal; }

    getTeachers() {
        return this.subjects.map(code => {
            if (!timetable.subjects[code]) return null;
            return timetable.subjects[code].getTeacher();
        }).filter(x => x);
    }

    getHouse() { return this.StudentHouse; }

    getYearLevel() {
        if (typeof this.StudentYearLevel === 'number') return this.StudentYearLevel;
        if (this.StudentYearLevel == null || this.StudentYearLevel === '') return null;
        // strip a leading-zero pad ("07"->"7") but keep a lone "0" (Reception).
        return String(this.StudentYearLevel).replace(/^0+(?=\d)/, '');
    }

    getTutorGroup() {
        if (this.StudentTutor) return this.StudentTutor;
        // Number() so a string-typed year ("9") matches, consistent with getYearLevel.
        if ([7, 8, 9, 10, 11, 12].indexOf(Number(this.StudentYearLevel)) !== -1) {
            return this.StudentHouse + this.getYearLevel();
        }
    }

    getTutorTeachers() {
        var tutorClass = this.getClasses().find(x => x.code.indexOf('TUT') !== -1);
        if (!tutorClass) return [];
        return tutorClass.getAllTeachers() || [];
    }

    getHeadOfHouse() {
        var house = this.getHouse();
        var year_level = this.getYearLevel();
        var houses = timetable.heads_of_house || {};
        var SchoolStaffCode;
        if ([7, 8, 9].includes(Number(year_level))) {
            SchoolStaffCode = houses[house] && houses[house][0];
        } else if ([10, 11, 12].includes(Number(year_level))) {
            SchoolStaffCode = houses[house] && houses[house][1];
        } else {
            return null;
        }
        return timetable.teachers[SchoolStaffCode];
    }

    getClasses() {
        return this.subjects.map(code => timetable.subjects[code]).filter(_ => _);
    }

    getTimeslots() {
        return this.getClasses().reduce((prev, curr) => prev.concat(curr.timeslots), []);
    }
}

// Some subjects are team-taught, where one teacher is listed as free in Synergetic
// but is actually busy teaching. These are exceptions that work around the source data.
const TEAM_TEACHER_SUBJECTS = ['Tutor', 'Creative Industries', 'Pastoral', 'Wellbeing'];

export class Teacher extends SchedulableEntity {
    constructor(data) {
        super();
        Object.assign(this, data);
        if (!this.subjects) this.subjects = [];
        if (!this.yardDuties) this.yardDuties = [];
        this.type = 'TEACHER';
    }

    // Teachers are the only entity whose "where are they" also includes duties.
    getDuties() {
        return this.yardDuties;
    }

    getName() {
        return this.StaffPreferredName + ' ' + this.StaffSurname;
    }

    getHouse() {
        var tutorClass = this.getClasses().find(c => c.getName().indexOf('Tutor') !== -1);
        if (!tutorClass) return null;
        return ['KM', 'BH', 'CN', 'MS'].find(house => tutorClass.getName().indexOf(house) !== -1);
    }

    getClasses() {
        return timetable.subjectsByTeacher[this.SchoolStaffCode] || [];
    }

    getTimeslots() {
        var subjects = this.getClasses();
        var timeslots = subjects.reduce((prev, curr) => prev.concat(curr.timeslots), []);

        // Keep only lessons this teacher actually teaches — unless the subject is a
        // known team-taught one (where Synergetic lists a busy teacher as free).
        timeslots = timeslots.filter(t =>
            t.teacher_short_name === this.SchoolStaffCode
            || TEAM_TEACHER_SUBJECTS.some(name => (t.ClassDescription || '').indexOf(name) !== -1)
        );

        // Sometimes you teach at least one lesson of a subject you aren't allocated to
        // (e.g. 03 TechnologiesS). Pick those up from the teacher index.
        (timetable.timeslotsByTeacher[this.SchoolStaffCode] || []).forEach(timeslot => {
            if (timeslots.indexOf(timeslot) === -1) timeslots.push(timeslot);
        });

        // Substitutions (pinkies easter egg) — empty unless enabled. Clone each
        // covered lesson and flag it isCover, so TimetableLesson shows the "[Pinkie
        // for …]" tag without mutating the original object (which is shared with the
        // covered teacher's own timetable).
        var mySubs = timetable.substitutions
            .filter(s => s.replacingTeacher && s.replacingTeacher.SchoolStaffCode === this.SchoolStaffCode)
            .map(s => s.getTimeslot())
            .filter(Boolean)
            .map(ts => Object.assign(Object.create(Object.getPrototypeOf(ts)), ts, { isCover: true }));
        return [...timeslots, ...mySubs];
    }

    getPhotoURL() {
        return fetchPhotoURL(this.StaffOccupEmail, 'teacher');
    }
}

export class Room extends SchedulableEntity {
    constructor(data) {
        super();
        Object.assign(this, data);
        this.type = 'ROOM';
    }

    getName() { return this.ROOM; }

    getTimeslots() {
        return timetable.timeslotsByRoom[this.CODE] || [];
    }

    isSeniorSchoolRoom() {
        return this.getTimeslots().some(t =>
            ['07', '08', '09', '10', '11', '12'].indexOf(String(t.class_code).substr(0, 2)) !== -1
        );
    }
}

export class ClassTimeslot {
    constructor(data) {
        Object.assign(this, data);
        this.type = 'CLASS_TIMESLOT';
    }
    getClass() { return timetable.subjects[this.class_code]; }
    getTeacher() { return timetable.teachers[this.teacher_short_name]; }
    getRoom() { return timetable.rooms[this.room_code]; }
    getLessonTime() {
        return { short_name: this.short_name, start: this.start, end: this.end, name: this.name };
    }
    toString() {
        var days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
        return days[Number(this.cycle_day_index) - 1] + ' ' + this.name;
    }
}

export class YardDutySlot {
    constructor(data) {
        Object.assign(this, data);
        this.type = 'YARD_DUTY_SLOT';
    }
    getID() {
        return 'YD' + this.DefinitionTimeFrom + '-' + this.DefinitionTimeTo;
    }
    getLessonTime() {
        return { short_name: this.short_name, start: this.start, end: this.end, name: this.name };
    }
    getTeacher() {
        return timetable.teachers[this.SchoolStaffCode];
    }
}

export class YardDutyLocation {
    constructor(data) {
        Object.assign(this, data);
        this.type = 'YARD_DUTY_LOCATION';
        if (!this.yard_duties) this.yard_duties = [];
    }
    getName() { return this.ClassDescription; }
}

// ---------------------------------------------------------------------------
// Grid projection — the period rows to render for a person on a given cycle day.
// Rows are the union of the definitions for every bell group the person's lessons
// belong to (so a cross-campus teacher gets both schedules), plus a row for each
// of their yard duties, deduped by (start, end, name) and sorted by start time.
// For the common single-group person this is exactly that group's periods, so the
// grid looks identical to before.
// ---------------------------------------------------------------------------

export function personGroups(person) {
    var groups = new Set();
    person.getTimeslots().forEach(t => { if (t.group) groups.add(t.group); });
    person.getDuties().forEach(d => { if (d.group) groups.add(d.group); });
    return groups;
}

export function periodRows(person) {
    var groups = personGroups(person);
    var rows = [];
    // Union of the person's groups' periods across the WHOLE week (not per day),
    // so every day column renders the identical row sequence — the same clock time
    // then lands on the same horizontal line in every column, regardless of which
    // bell schedule a given day is drawn from.
    Object.keys(timetable.definitionsByGroupDay).forEach(key => {
        var group = key.split('|')[0];
        if (groups.has(group)) {
            timetable.definitionsByGroupDay[key].forEach(d => rows.push(d));
        }
    });
    // Every yard duty (any day) also earns a shared row. Tag it isDuty so the ruler
    // floors it to a readable height even when its name ("Recess Duty") maps to a
    // break short_name (R/L/B).
    person.getDuties().forEach(d => rows.push({
        rowKey: d.rowKey, name: d.name, short_name: d.short_name, isDuty: true,
        start: d.start, end: d.end, startMin: d.startMin, endMin: d.endMin
    }));
    // Safety net: a lesson whose definition carried no `group` was never added by the
    // group loop above; add a row for every timeslot the person has, so no rendered
    // event is left without a ruler row (which would clamp it to the day's bottom and
    // overlap other clamped events). Deduped by rowKey below, so grouped lessons —
    // already present — cost nothing.
    person.getTimeslots().forEach(t => {
        if (t.startMin != null && t.endMin != null) rows.push({
            rowKey: t.rowKey, name: t.name, short_name: t.short_name,
            start: t.start, end: t.end, startMin: t.startMin, endMin: t.endMin
        });
    });
    rows.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
    var seen = new Set();
    var out = [];
    for (var r of rows) {
        if (!seen.has(r.rowKey)) { seen.add(r.rowKey); out.push(r); }
    }
    return out;
}

// Global period rows across every bell group (for the aggregate/master views).
// Pass { lessonsOnly: true } to drop the breaks/recess/lunch rows.
export function allPeriodRows(options) {
    options = options || {};
    var rows = [];
    Object.values(timetable.definitionsByGroupDay).forEach(list => list.forEach(d => rows.push(d)));
    rows.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
    var seen = new Set();
    var out = [];
    for (var r of rows) {
        if (seen.has(r.rowKey)) continue;
        if (options.lessonsOnly && ['R', 'L', 'B'].indexOf(r.short_name) !== -1) continue;
        seen.add(r.rowKey);
        out.push(r);
    }
    return out;
}
